/**
 * CRT composite. Runs at output resolution and writes to the default framebuffer.
 *
 * Two sampling regimes share this shader and must not be confused:
 *
 *  - The scene, the persistence trail, the bloom and the halation are sampled through
 *    the barrel warp. Pixels stay chunky; the scene target is nearest filtered.
 *  - The aperture grille, the scanlines, the vignette and the grain are evaluated in
 *    continuous *output* space. A real shadow mask is finer than a source pixel;
 *    computing it at buffer resolution gives three fat stripes per sprite instead of a
 *    phosphor structure.
 *
 * Order matters. Every light-forming term happens in linear HDR and the tonemap comes
 * after all of them; tonemap earlier and the bloom skirt clips to white, which is the
 * failure the whole stack exists to avoid.
 *
 * Three guards live at the bottom and are acceptance gates, not taste:
 *  - §11.8 photosafety — the full-screen flash is applied *after* the tonemap, so its
 *    contribution to displayed luminance is exactly `uFlash.a`, which the CPU side caps
 *    at 0.35 and rate-limits to 3/s.
 *  - AMD-3 — scanline strength defaults to 0.18 and is capped at 0.35, because a
 *    full-strength grille costs the §11.9 telegraph-edge contrast ratio.
 *  - The grille is applied to the tonemapped picture and renormalised to constant
 *    luminance, so it redistributes energy between channels rather than dimming a
 *    telegraph edge that happens to land on a dark stripe.
 */

import { GLSL_COLOR } from './common';

export const CRT_COMPOSITE_FRAG = /* glsl */ `
precision highp float;

uniform sampler2D tScene;
uniform sampler2D tPersist;
uniform sampler2D tBloom;
uniform sampler2D tHalation;

uniform vec2 uOutputSize;
uniform vec2 uBufferSize;
uniform float uTime;

uniform float uPersistMix;
uniform float uBloomIntensity;
uniform float uHalationStrength;
uniform vec3 uHalationTint;
uniform float uExposure;

uniform vec2 uCurvature;
uniform float uOverscan;
uniform float uChromaticAberration;

uniform float uMaskPitch;
uniform float uMaskStrength;
uniform float uScanlineStrength;
uniform float uScanlineSharpness;
uniform float uScanlinePitch;
uniform float uEdgeBlur;
uniform float uMaskFade;

uniform float uVignette;
uniform float uNoise;

/** AMD-6 D16: 0 at real time, 1 at full bullet time. */
uniform float uSlowmo;
/** AMD-6 D5: xy = player centre in buffer uv, z = rim radius in uv-y units. */
uniform vec3 uPlayerRim;
uniform vec3 uPlayerRimColor;

/** rgb = flash colour, a = displayed-luminance contribution (§11.8 cap 0.35). */
uniform vec4 uFlash;
/** Fraction of the frame height covered by each cinematic bar. */
uniform float uLetterbox;

in vec2 vUv;
out vec4 fragColor;

${GLSL_COLOR}

#define TAU 6.28318530718

float saturate1(float x) { return clamp(x, 0.0, 1.0); }

// Separable barrel distortion. Overscan pulls the sampled region inward first, so the
// warp can push outward without ever reaching past the buffer edge.
vec2 warp(vec2 uv) {
  vec2 c = (uv * 2.0 - 1.0) / uOverscan;
  float x2 = c.x * c.x;
  float y2 = c.y * c.y;
  c.x *= 1.0 + uCurvature.x * y2;
  c.y *= 1.0 + uCurvature.y * x2;
  return c * 0.5 + 0.5;
}

// Current frame or its own afterglow, whichever is brighter. The trail adds light; it
// never takes any away.
vec3 tube(vec2 uv) {
  vec3 cur = texture(tScene, uv).rgb;
  vec3 trail = texture(tPersist, uv).rgb * uPersistMix;
  return max(cur, trail);
}

void main() {
  vec2 w = warp(vUv);

  vec2 soft = 1.5 / uOutputSize;
  vec2 edge = smoothstep(vec2(0.0), soft, w) * smoothstep(vec2(0.0), soft, 1.0 - w);
  float inside = edge.x * edge.y;

  float cx = vUv.x * 2.0 - 1.0;
  // AMD-6 D16: bullet time pushes the edge aberration to ~2 px. At rest this is the
  // AMD-3 baseline and nothing changes.
  float aberration = uChromaticAberration + 2.0 * uSlowmo * abs(cx);
  vec2 ca = vec2(aberration * cx / uOutputSize.x, 0.0);

  vec3 col;
  col.r = tube(w + ca).r;
  col.g = tube(w).g;
  col.b = tube(w - ca).b;

  // Lomo edge softness. A cheap camera holds focus in the middle of the frame and loses
  // it toward the corners, so the outer field gets a four-tap box whose radius grows with
  // the squared radius. Four taps rather than a real kernel because this runs on every
  // pixel of every frame; uEdgeBlur is zeroed on the lower tiers by the governor, which
  // is also what keeps the artefact from eating a handheld's fill rate.
  vec2 cc = vUv * 2.0 - 1.0;
  float rr = dot(cc, cc);
  float soften = smoothstep(0.42, 1.55, rr);
  if (uEdgeBlur * soften > 0.002) {
    // Two taps on the scene alone, on one diagonal. The first cut of this took four taps
    // through tube(), which samples the persistence buffer as well — eight fetches per
    // pixel across the outer field, and the bench measured it at +16% on p95 frame time.
    // A diagonal pair is what a cheap lens actually does at the corners anyway: it smears
    // along an axis rather than defocusing evenly, and the trail layer contributes nothing
    // to a blur that is already reading a neighbourhood.
    vec2 o = (uEdgeBlur * soften) / uOutputSize;
    vec3 blurred = texture(tScene, w + o).rgb + texture(tScene, w - o).rgb;
    col = mix(col, blurred * 0.5, min(0.7, soften));
  }

  col += texture(tBloom, w).rgb * uBloomIntensity;
  col += texture(tHalation, w).rgb * uHalationTint * uHalationStrength;

  // §11.1 exposure, settings range 0.9–1.4.
  col *= uExposure;

  // AMD-6 D16: bullet time desaturates the *background* by 35% and leaves the bright
  // gameplay layer alone, so the frame cools without the threats losing their hue.
  if (uSlowmo > 0.0) {
    float l = luma(col);
    float bg = 1.0 - smoothstep(0.06, 0.45, l);
    col = mix(col, vec3(l), 0.35 * uSlowmo * bg);
  }

  vec3 grille = (0.5 + 0.5 * cos(TAU * gl_FragCoord.x / uMaskPitch - vec3(0.0, 2.09439510239, 4.18879020479))) * 2.0;
  vec3 mask = mix(vec3(1.0), grille, uMaskStrength * uMaskFade);

  // Scanlines are measured in OUTPUT pixels, not buffer rows. Against the buffer the
  // period was one source row, so on any display where a source row is under a device
  // pixel the grille aliased into moire and AMD-6 D12 answered by switching it off below
  // a 900 px buffer — which is every phone, and most windows. A pitch fixed in device
  // pixels cannot alias with the source at all, so the artefact survives at any size and
  // stays what it was asked to be: faint structure, not a pattern fighting the pixels.
  float row = gl_FragCoord.y / max(uScanlinePitch, 1.0);
  float band = pow(0.5 - 0.5 * cos(TAU * fract(row)), uScanlineSharpness);
  col *= mix(1.0, mix(1.0 - uScanlineStrength, 1.0, band), uMaskFade);

  // AMD-6 D12: the vignette is pulled inward. At the old 0.25 power it was already
  // eating the outer 15% of the frame — which is exactly where an off-screen threat
  // first appears — so the falloff now stays near 1.0 until the outer 12%.
  float vig = (1.0 - uVignette)
            + uVignette * pow(saturate1(16.0 * w.x * w.y * (1.0 - w.x) * (1.0 - w.y)), 0.12);
  // AMD-6 D16: the corners run 12% cooler during bullet time.
  col *= vig;
  col *= mix(vec3(1.0), vec3(0.88, 0.95, 1.12), uSlowmo * (1.0 - vig) / max(uVignette, 1e-4));

  float n = hash12(gl_FragCoord.xy + uTime * 137.0) - 0.5;
  col *= 1.0 + n * uNoise * 0.5;
  col += n * uNoise * 0.5;

  col *= inside;

  vec3 shown = acesFilm(max(col, vec3(0.0)));

  // Grille applied to the displayed value and pinned to constant luminance. Applying it
  // before the tonemap flattens it for grey and grey alone: acesFilm is hue-preserving,
  // so a chroma swing at constant scene luminance still arrives as a brightness swing.
  vec3 tinted = shown * mask;
  tinted *= luma(shown) / max(luma(tinted), 1e-4);

  float hi = max(tinted.r, max(tinted.g, tinted.b));
  float sh = max(shown.r, max(shown.g, shown.b));
  if (hi > 1.0) tinted = mix(tinted, shown, (hi - 1.0) / max(hi - sh, 1e-4));

  // §11.8: post-tonemap so the added luminance is exactly the capped amount.
  tinted = mix(tinted, uFlash.rgb, clamp(uFlash.a, 0.0, 0.35));

  // AMD-6 D5: a 2 px pure-white rim on the player, drawn AFTER the tonemap, the grille,
  // the scanlines and the vignette. Nothing in the post chain can dilute it, which is
  // what makes the player unambiguously the brightest object in every frame.
  if (uPlayerRim.z > 0.0) {
    float ar = uOutputSize.x / max(uOutputSize.y, 1.0);
    vec2 pd = (w - uPlayerRim.xy) * vec2(ar, 1.0);
    float px = 1.0 / uOutputSize.y;
    float d = length(pd) - uPlayerRim.z;
    // AMD-7 R3: 3 px of pure white with a 1 px black contour on its INSIDE edge. A
    // 1.5 px white ring vanished into a blown-out background; the black contour is what
    // holds it against a bloomed friendly projectile as well as against the abyss.
    float contour = 1.0 - smoothstep(0.5 * px, 1.6 * px, abs(d + 2.0 * px));
    tinted = mix(tinted, vec3(0.0), contour * 0.9 * inside);
    float ring = 1.0 - smoothstep(1.5 * px, 2.6 * px, abs(d));
    tinted = mix(tinted, uPlayerRimColor, ring * inside);
  }

  // Cinematic bars (§13.1.7 boss framing). Drawn last so nothing bleeds into them.
  float bar = step(vUv.y, uLetterbox) + step(1.0 - uLetterbox, vUv.y);
  tinted *= 1.0 - clamp(bar, 0.0, 1.0);

  fragColor = vec4(linearToSrgb(clamp(tinted, 0.0, 1.0)), 1.0);
}
`;

/** The prescale blit: the whole filter lives in the target's sampler state, not here. */
export const BLIT_FRAG = /* glsl */ `
precision highp float;
uniform sampler2D tSrc;
in vec2 vUv;
out vec4 fragColor;
void main() { fragColor = texture(tSrc, vUv); }
`;
